import { useEffect } from 'react'
import { Outlet, useLocation, Link, useLoaderData } from 'react-router'
import type { MetaFunction } from 'react-router'
import { Wrench } from 'lucide-react'
import { sql } from 'drizzle-orm'
import { db } from '@afficheservices/db'
import { leads, reviews } from '@afficheservices/db/schema'
import { ExitIntentModal } from '~/components/exit-intent'
import { DevisProgressBar } from '~/components/devis-progress-bar'
import { TrustStrip } from '~/components/trust-strip'
import { FunnelSocialProofRail } from '~/components/funnel-social-proof-rail'

export const meta: MetaFunction = () => [
  { title: 'Demander un devis gratuit — AfficheServices' },
  { name: 'description', content: "Recevez jusqu'à 3 devis gratuits de professionnels vérifiés en 48h. Service 100% gratuit et sans engagement." },
  // Layout-level robots tag: RR7 children without their own meta() inherit
  // this one, which covers steps 1–5. The file already claimed "funnel pages
  // are noindex" but nothing emitted the tag, so /demande-devis/2 … /5 were
  // indexable 450-word shells. Steps 6 and /confirmation override meta() and
  // carry their own copy.
  { name: 'robots', content: 'noindex, follow' },
]

/**
 * Funnel-wide social-proof stats. Funnel pages are noindex so there is no
 * SEO impact. Queries are bounded and indexed; failures fall back to zeros
 * so the rail is hidden rather than blocking the funnel.
 */
export async function loader() {
  // 30-day window catches volume on a low-traffic launch site that 7-day
  // misses entirely. Honest signal once the rail says "X devis traités ces
  // 30 derniers jours" instead of "cette semaine".
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  try {
    const [leadRow, ratingRow] = await Promise.all([
      db
        .select({ count: sql<number>`count(*)::int` })
        .from(leads)
        .where(sql`${leads.createdAt} >= ${thirtyDaysAgo}`),
      db
        .select({
          avgRating: sql<number>`AVG(${reviews.overallRating})::float`,
          totalReviews: sql<number>`count(*)::int`,
        })
        .from(reviews)
        .where(sql`${reviews.status} = 'approved'`),
    ])
    return {
      recentLeadCount: leadRow[0]?.count ?? 0,
      avgRating: ratingRow[0]?.avgRating ?? null,
      totalReviews: ratingRow[0]?.totalReviews ?? 0,
    }
  } catch {
    return { recentLeadCount: 0, avgRating: null, totalReviews: 0 }
  }
}

export default function DemandeDevisLayout() {
  const { recentLeadCount, avgRating, totalReviews } = useLoaderData<typeof loader>()
  const location = useLocation()
  const stepMatch = location.pathname.match(/demande-devis\/(\d)/)
  const currentStep = stepMatch ? parseInt(stepMatch[1]) : 1
  const isConfirmation = location.pathname.includes('confirmation')

  // Save flow progress so user can resume if they leave
  useEffect(() => {
    if (!isConfirmation && location.search) {
      try { sessionStorage.setItem('devis_progress', location.pathname + location.search) } catch {}
    }
    if (isConfirmation) {
      try { sessionStorage.removeItem('devis_progress') } catch {}
    }
  }, [location.pathname, location.search, isConfirmation])

  return (
    <div className="flex min-h-screen flex-col bg-white">
      {/* Minimal header — Angi style */}
      <header className="flex h-14 items-center justify-center border-b border-slate-100">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary">
            <Wrench className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="text-lg font-bold text-slate-900">
            Affiche<span className="text-primary">Services</span>
          </span>
        </Link>
      </header>

      {/* Progress bar — stages with icons */}
      {!isConfirmation && <DevisProgressBar current={currentStep} />}

      {/*
        Content — centered, max-width constrained.
        CRO mobile_ux_3: MobileBottomNav is suppressed inside /demande-devis
        (see components/mobile-nav.tsx) to eliminate 4 one-tap exits during the
        fragile multi-step conversion. Keeping pb-24 / lg:pb-10 here is harmless
        and preserves the layout if the suppression is ever reverted.
      */}
      <main className="flex flex-1 flex-col items-center px-4 pb-24 pt-10 lg:pb-10">
        <div className="w-full max-w-lg">
          {!isConfirmation && (
            <div className="mb-6">
              <TrustStrip recentLeadCount={recentLeadCount} />
            </div>
          )}
          {!isConfirmation && currentStep >= 2 && (
            <div className="mb-4">
              <FunnelSocialProofRail
                avgRating={avgRating}
                totalReviews={totalReviews}
                recentLeadCount={recentLeadCount}
              />
            </div>
          )}
          <Outlet context={{ avgRating, totalReviews, recentLeadCount }} />
        </div>
      </main>

      {/* Exit intent — shows when user tries to leave steps 2-5 of 6-step funnel */}
      {!isConfirmation && <ExitIntentModal currentStep={currentStep} totalSteps={6} />}

      {/* Minimal footer */}
      <footer className="border-t border-slate-100 py-4 text-center text-xs text-slate-400">
        &copy; 2026 AfficheServices · Service gratuit et sans engagement
      </footer>
    </div>
  )
}
